import { useCallback, useEffect, useRef, useState } from "react";

interface UseInfiniteScrollOptions {
  /** The scroll container used as the IntersectionObserver root (null = viewport). */
  rootRef: React.RefObject<HTMLElement>;
  hasMore: boolean;
  isLoadingMore: boolean;
  loadMore: () => void;
  /** Distance from the bottom (px) at which to prefetch the next page. */
  rootMargin?: string;
}

/**
 * Gradual infinite scroll: loads exactly one more page each time the sentinel
 * scrolls into view near the bottom. One page per trigger — not the whole set
 * at once — so a client's older items load as the user scrolls toward them
 * instead of hiding behind a "Load more" button below the fold.
 *
 * Returns a callback ref to attach to a sentinel element rendered after the
 * grid. Tracking the node in state lets the observer (re)bind whenever the
 * grid mounts/unmounts, and runs the setup effect after refs are attached so
 * the scroll-container root is available.
 */
export function useInfiniteScroll({
  rootRef,
  hasMore,
  isLoadingMore,
  loadMore,
  rootMargin = "400px",
}: UseInfiniteScrollOptions) {
  const [sentinel, setSentinel] = useState<HTMLDivElement | null>(null);
  const sentinelRef = useCallback((node: HTMLDivElement | null) => {
    setSentinel(node);
  }, []);

  // Latest flags/callback without re-creating the observer each render.
  const stateRef = useRef({ hasMore, isLoadingMore, loadMore });
  stateRef.current = { hasMore, isLoadingMore, loadMore };

  useEffect(() => {
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const { hasMore, isLoadingMore, loadMore } = stateRef.current;
        if (entries[0]?.isIntersecting && hasMore && !isLoadingMore) {
          loadMore();
        }
      },
      { root: rootRef.current ?? null, rootMargin }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, rootRef, rootMargin]);

  return sentinelRef;
}
